/* Бизнесы. ТЗ 6.3 — фокус, факторы, воронка аккаунтинга, продукты. */

import { accStageColor, accStagesOf, isClosedStage, projZone, revZone, rub, stageTint } from "@mera/shared";
import React, { useState } from "react";
import { useNavigate } from "../lib/router.jsx";

import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelBusinesses } from "../components/skeleton.jsx";
import {
  Empty, ErrorState, Eyebrow, HealthDot, Hint, Metric, MetricGrid, Money, Pill, Sep,
} from "../components/ui.jsx";
import { useApp, useFetch } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";

export default function Businesses() {
  const { meta } = useApp();
  const { data: projects, loading, error, refetch } = useFetch("/projects");
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  if (loading) return <Screen back><SkelBusinesses /></Screen>;
  if (error) return <Screen back><ErrorState text={error} onRetry={refetch} /></Screen>;

  return (
    <Screen back>
      <div className="px-4 pt-1 flex flex-col gap-2">
        {(meta?.businesses || []).map((b) => {
          /* Проект заводится вместе со сделкой, поэтому здесь оставляем только те,
             что уже в работе: дошли до аккаунтинга либо живут без сделки. Иначе
             у бизнеса висели бы все лиды воронки, а «Чек» считал бы непроданное. */
          const mine = projects.filter((p) => p.businessId === b.id && (p.accStage || !p.dealId));
          const active = mine.filter((p) => p.accStage && !isClosedStage(p.accStage));
          const income = mine.reduce((s, p) => s + p.income, 0);
          const expenses = mine.reduce((s, p) => s + p.expenses, 0);
          const isOpen = open === b.id;
          const zone = active.length ? projZone(active.length) : revZone(income);

          return (
            <div key={b.id} className="card-tg overflow-hidden">
              <button
                className="w-full text-left px-4 py-3 active:opacity-70"
                onClick={() => { haptic.light(); setOpen(isOpen ? null : b.id); }}
              >
                <div className="flex items-center gap-2.5">
                  <HealthDot t={zone.t} size={9} />
                  <div className="min-w-0 flex-1">
                    <div className="display text-[17px] font-semibold truncate">{b.name}</div>
                    <div className="text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                      {[b.sub, b.owner].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Ion.chevronDown
                    size={17}
                    style={{ color: "var(--tg-hint)", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}
                  />
                </div>

                <div className="mt-2 flex items-start gap-2">
                  <Ion.locate size={13} style={{ color: "var(--fin-gold)" }} className="mt-0.5 shrink-0" />
                  <span className="text-[13px] leading-snug">{b.focus}</span>
                </div>
              </button>

              {isOpen ? (
                <div className="rise pb-2">
                  <Sep />
                  <div className="grid grid-cols-3 gap-2 px-4 py-3">
                    <Mini label="Проектов" value={active.length} />
                    <Mini label="Чек" value={rub(income)} />
                    <Mini label="Себестоимость" value={rub(expenses)} color="var(--fin-red)" />
                  </div>

                  {b.factors?.length ? (
                    <>
                      <div className="eyebrow px-4 pt-1 pb-1">Требует решения</div>
                      <div className="px-4 flex flex-col gap-1.5 pb-2">
                        {b.factors.map((f, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <Ion.flame size={12} style={{ color: "var(--fin-red)" }} className="mt-0.5 shrink-0" />
                            <span className="text-[13px] leading-snug">{f}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}

                  {mine.length ? (
                    <>
                      <div className="eyebrow px-4 pt-1 pb-1">Проекты</div>
                      {mine.map((p, i) => {
                        const stages = accStagesOf(b);
                        return (
                          <React.Fragment key={p.id}>
                            {i > 0 ? <Sep /> : null}
                            <button
                              className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 active:opacity-60"
                              onClick={() => { haptic.light(); navigate(`/project/${p.id}`); }}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{
                                  background: p.accStage
                                    ? stageTint(b, "acc", p.accStage, accStageColor(p.accStage, stages))
                                    : "var(--tg-hint)",
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-[14px] font-medium truncate">{p.name}</div>
                                <Hint>{p.accStage || p.stage} · {p.type}</Hint>
                              </div>
                              <span className="num text-[13px] font-semibold shrink-0">{rub(p.income)}</span>
                              <Ion.chevronForward size={15} style={{ color: "var(--tg-hint)" }} />
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </>
                  ) : (
                    <Empty text="Проектов пока нет" />
                  )}

                  {accStagesOf(b).length ? (
                    <div className="px-4 pt-2">
                      <button
                        className="w-full rounded-lg py-2 text-[14px] font-semibold active:opacity-70"
                        style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)", color: "var(--tg-button)" }}
                        onClick={() => navigate(`/crm?funnel=${b.id}&stream=accounting`)}
                      >
                        Открыть воронку аккаунтинга →
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Screen>
  );
}

const Mini = ({ label, value, color }) => (
  <div>
    <div className="eyebrow px-0 pt-0 pb-0.5">{label}</div>
    <div className="num text-[15px] font-bold" style={{ color: color || "var(--tg-text)" }}>{value}</div>
  </div>
);
